/**
 * Which company a workbook's Bifrost cells belong to.
 *
 * The add-in has one sign-in for every open workbook (OfficeRuntime.storage), so a company switch in
 * one workbook's pane changes the company every other open workbook would compute with. A workbook
 * is therefore bound to the company its first Bifrost request was made for, in the workbook's own
 * settings, and its cells refuse to compute for any other: another company's figure in a cell looks
 * exactly like the right one.
 */

export const WORKBOOK_COMPANY_SETTING = 'bifrost.workbookCompany';
/**
 * A recalculation the cells still owe the company the workbook belongs to, after a move or a binding.
 * Kept in the file until it has run: a workbook saved before then reopens with the old figures.
 */
export const RECALCULATION_PENDING_SETTING = 'bifrost.recalculationPending';

/** How long a cell waits for Office to initialise before it gives up on reading the binding. */
const OFFICE_READY_TIMEOUT_MS = 5000;

export interface WorkbookCompany {
  companyId: string;
  /** As the add-in knew it when binding; empty when it did not know the name. */
  companyName: string;
}

export interface CompanyIdentity {
  companyId: string;
  companyName?: string;
}

export type RecalculationCause = 'binding' | 'move';

export interface PendingRecalculation {
  companyId: string;
  cause: RecalculationCause;
}

/** The part of Office.Settings the binding uses. */
export interface WorkbookSettings {
  get(name: string): unknown;
  set(name: string, value: unknown): void;
  remove?(name: string): void;
  saveAsync(callback?: (result: { status: unknown; error?: { message?: string } }) => void): void;
}

/**
 * What a cell may do. `unreadable`: the workbook's settings could not be read, so whether it is bound,
 * and to whom, is unknown. Computing then could put another company's figure in a workbook the file
 * binds; the cell asks for a recalculation instead. `unsupported`: this runtime has no workbook
 * settings at all, so nothing can be bound or checked, and recalculating does not change that.
 */
export type WorkbookVerdict =
  | { kind: 'proceed'; boundNow: boolean }
  | { kind: 'refuse'; bound: WorkbookCompany }
  | { kind: 'unreadable' }
  | { kind: 'unsupported' };

/** `recalculateFailed`: the cells could not be recalculated after a cell's first request tied the workbook. */
export type WorkbookCompanyEvent = { kind: 'changed' } | { kind: 'recalculateFailed' };

type Listener = (event: WorkbookCompanyEvent) => void;

interface WorkbookSession {
  /**
   * The binding as this runtime last set it, which wins over the settings: it is the latest decision,
   * also where the settings could not be written. Bindings are only made where the settings could be
   * read, or by a choice in the pane, so it never hides a binding the file holds.
   */
  company?: WorkbookCompany;
  /** Set in the settings but not yet saved in the file; the pane saves it again. */
  unsaved?: boolean;
  /** Bound by a cell this session, not chosen in the pane: the pane says so, with the way out. */
  tiedByCell?: boolean;
  listeners?: Listener[];
  /** Office has initialised in this runtime. */
  officeInitialised?: boolean;
  /** Run once, if Office initialises only after a cell stopped waiting for it. */
  lateReady?: Array<() => void>;
  lateReadyRan?: boolean;
}

interface SessionHolder {
  __bifrostWorkbook?: WorkbookSession;
}

/**
 * On globalThis because the functions bundle and the task-pane bundle each load their own copy of
 * this module into the one runtime, and the runtime belongs to one open workbook.
 */
function session(): WorkbookSession {
  const holder = globalThis as unknown as SessionHolder;
  if (!holder.__bifrostWorkbook) holder.__bifrostWorkbook = {};
  return holder.__bifrostWorkbook;
}

let ready: Promise<boolean> | null = null;

function officePresent(): boolean {
  return typeof Office !== 'undefined' && typeof Office.onReady === 'function';
}

/**
 * Office's document settings exist only once Office has initialised; false when it never did. A wait
 * that gave up is tried again by the next call, and Office initialising after all runs what
 * `onOfficeReadyLate` registered.
 */
export function officeReady(): Promise<boolean> {
  if (!ready) {
    ready = !officePresent()
      ? Promise.resolve(false)
      : new Promise<boolean>((resolve) => {
        let gaveUp = false;
        const timer = setTimeout(() => {
          gaveUp = true;
          ready = null;
          resolve(false);
        }, OFFICE_READY_TIMEOUT_MS);
        Office.onReady(() => {
          clearTimeout(timer);
          session().officeInitialised = true;
          resolve(true);
          if (gaveUp) readyLate();
        });
      });
  }
  return ready.then((initialised) => {
    if (initialised) session().officeInitialised = true;
    return initialised;
  });
}

/** Runs `listener` once, when Office initialises only after a cell had stopped waiting for it. */
export function onOfficeReadyLate(listener: () => void): void {
  const state = session();
  state.lateReady = [...(state.lateReady ?? []), listener];
}

function readyLate(): void {
  const state = session();
  if (state.lateReadyRan) return;
  state.lateReadyRan = true;
  (state.lateReady ?? []).forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.warn('Could not act on Office initialising late:', error);
    }
  });
}

/** The workbook's own settings; both bundles reach the same object through the Office global. */
export function workbookSettings(): WorkbookSettings | null {
  try {
    if (typeof Office === 'undefined') return null;
    return (Office.context?.document?.settings as WorkbookSettings | undefined) ?? null;
  } catch {
    return null;
  }
}

function asWorkbookCompany(value: unknown): WorkbookCompany | null {
  const candidate = value as Partial<WorkbookCompany> | null | undefined;
  if (!candidate || typeof candidate.companyId !== 'string' || !candidate.companyId) return null;
  return {
    companyId: candidate.companyId,
    companyName: typeof candidate.companyName === 'string' ? candidate.companyName : '',
  };
}

type Reading = { readable: true; company: WorkbookCompany | null } | { readable: false; forGood: boolean };

/**
 * No settings object where there will never be one: no Office at all, or Office initialised without
 * workbook settings, as in the separate custom-functions runtime of an add-in manifest from before the
 * shared runtime. Otherwise the settings are only not there yet. The one place that tells them apart.
 */
function settingsMissingForGood(): boolean {
  return !officePresent() || session().officeInitialised === true;
}

function read(settings: WorkbookSettings | null): Reading {
  const own = session().company;
  if (own) return { readable: true, company: own };
  if (!settings) return { readable: false, forGood: settingsMissingForGood() };
  try {
    return { readable: true, company: asWorkbookCompany(settings.get(WORKBOOK_COMPANY_SETTING)) };
  } catch {
    return { readable: false, forGood: false };
  }
}

function unread(reading: Extract<Reading, { readable: false }>): WorkbookVerdict {
  return reading.forGood ? { kind: 'unsupported' } : { kind: 'unreadable' };
}

/** The company this workbook is bound to; null while unbound, or while its settings cannot be read. */
export function workbookCompany(settings: WorkbookSettings | null = workbookSettings()): WorkbookCompany | null {
  const reading = read(settings);
  return reading.readable ? reading.company : null;
}

/** Whether a cell bound this workbook this session, as against a choice made in the pane. */
export function tiedByCell(): boolean {
  return session().tiedByCell === true;
}

/** Hears every change of binding, and a recalculation after one that failed. Returns the unsubscribe. */
export function onWorkbookCompanyEvent(listener: Listener): () => void {
  const state = session();
  state.listeners = [...(state.listeners ?? []), listener];
  return () => {
    state.listeners = (state.listeners ?? []).filter(l => l !== listener);
  };
}

/** Delivered after the current call stack, which may be a custom function's evaluation. */
export function emitWorkbookCompanyEvent(event: WorkbookCompanyEvent): void {
  const listeners = session().listeners ?? [];
  if (listeners.length === 0) return;
  setTimeout(() => listeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      console.warn('Workbook company listener failed:', error);
    }
  }), 0);
}

function save(company: WorkbookCompany, settings: WorkbookSettings | null): void {
  const state = session();
  if (!settings) {
    state.unsaved = true;
    return;
  }
  try {
    settings.set(WORKBOOK_COMPANY_SETTING, company);
    state.unsaved = true;
    settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        console.warn('Could not save the workbook company:', result.error?.message);
        return;
      }
      // A later binding may have been set while this one was saving.
      if (session().company === company) session().unsaved = false;
    });
  } catch (error) {
    state.unsaved = true;
    console.warn('Could not record the workbook company:', error);
  }
}

/** Binds this workbook to `company`: at once for this session, and in the file with its next save. */
export function bindWorkbook(company: WorkbookCompany, settings: WorkbookSettings | null = workbookSettings()): void {
  const state = session();
  state.company = company;
  state.tiedByCell = false;
  save(company, settings);
  emitWorkbookCompanyEvent({ kind: 'changed' });
}

/**
 * Whether a cell may compute for `current`, binding nothing: a workbook bound to another company is
 * refused, and one whose settings cannot be read waits. Signed out (`null`) passes, so the request
 * answers #SIGN_IN.
 */
export function checkWorkbook(current: string | null, settings: WorkbookSettings | null = workbookSettings()): WorkbookVerdict {
  if (current === null) return { kind: 'proceed', boundNow: false };
  const reading = read(settings);
  if (!reading.readable) return unread(reading);
  if (reading.company && reading.company.companyId !== current) return { kind: 'refuse', bound: reading.company };
  return { kind: 'proceed', boundNow: false };
}

/**
 * Whether recalculating this workbook now computes its cells for `companyId`, the company the add-in
 * is signed in to: yes when the workbook belongs to it or to no company yet. Not for another company,
 * nor while the company is unknown or the settings cannot be read: the cells would only turn their
 * saved figures into errors.
 */
export function mayRecalculateFor(companyId: string | null | undefined, settings: WorkbookSettings | null = workbookSettings()): boolean {
  return Boolean(companyId) && checkWorkbook(companyId ?? null, settings).kind === 'proceed';
}

/**
 * Whether a request may go out for `current`, the company of the token it carries: yes when the
 * workbook is bound to that company, and an unbound workbook is bound to it now. Any other company is
 * refused, and a workbook whose settings cannot be read is not bound by guesswork.
 */
export function claimWorkbook(current: CompanyIdentity, settings: WorkbookSettings | null = workbookSettings()): WorkbookVerdict {
  const reading = read(settings);
  if (!reading.readable) return unread(reading);
  const bound = reading.company;
  if (!bound) {
    bindWorkbook({ companyId: current.companyId, companyName: current.companyName ?? '' }, settings);
    session().tiedByCell = true;
    return { kind: 'proceed', boundNow: true };
  }
  if (bound.companyId !== current.companyId) return { kind: 'refuse', bound };
  if (current.companyName) nameWorkbookCompany(current.companyName, settings);
  return { kind: 'proceed', boundNow: false };
}

/** Fills in the name of the company the workbook is bound to, where it was bound without one. */
export function nameWorkbookCompany(companyName: string, settings: WorkbookSettings | null = workbookSettings()): WorkbookCompany | null {
  const bound = workbookCompany(settings);
  if (!bound || bound.companyName || !companyName) return bound;
  const tied = tiedByCell();
  const named = { companyId: bound.companyId, companyName };
  bindWorkbook(named, settings);
  session().tiedByCell = tied;
  return named;
}

/** A company chosen in this workbook's pane becomes the workbook's, bound or not. */
export function moveWorkbookTo(company: WorkbookCompany, settings: WorkbookSettings | null = workbookSettings()): void {
  const bound = workbookCompany(settings);
  if (bound && bound.companyId === company.companyId && bound.companyName === company.companyName) {
    session().tiedByCell = false;
    return;
  }
  bindWorkbook(company, settings);
}

/**
 * Saves the session's binding again where the file does not hold it yet: a save that failed, or a
 * settings write that threw. Answers whether it tried.
 */
export function persistWorkbookCompany(settings: WorkbookSettings | null = workbookSettings()): boolean {
  const state = session();
  const company = state.company;
  if (!company || !settings) return false;
  let stored: WorkbookCompany | null;
  try {
    stored = asWorkbookCompany(settings.get(WORKBOOK_COMPANY_SETTING));
  } catch {
    return false;
  }
  const differs = !stored || stored.companyId !== company.companyId || stored.companyName !== company.companyName;
  if (!differs && !state.unsaved) return false;
  save(company, settings);
  return true;
}

function saveSettings(settings: WorkbookSettings, what: string): void {
  settings.saveAsync((result) => {
    if (result.status === Office.AsyncResultStatus.Failed) console.warn(`Could not save ${what}:`, result.error?.message);
  });
}

/** Records in the file that the cells still owe `pending.companyId` a recalculation. */
export function markRecalculationPending(pending: PendingRecalculation, settings: WorkbookSettings | null = workbookSettings()): void {
  if (!settings) return;
  try {
    settings.set(RECALCULATION_PENDING_SETTING, pending);
    saveSettings(settings, 'the pending recalculation');
  } catch (error) {
    console.warn('Could not record the pending recalculation:', error);
  }
}

/** Every cell has been recalculated: the file owes nothing any more. */
export function clearRecalculationPending(settings: WorkbookSettings | null = workbookSettings()): void {
  if (!settings) return;
  try {
    if (settings.get(RECALCULATION_PENDING_SETTING) == null) return;
    if (settings.remove) settings.remove(RECALCULATION_PENDING_SETTING);
    else settings.set(RECALCULATION_PENDING_SETTING, null);
    saveSettings(settings, 'the finished recalculation');
  } catch (error) {
    console.warn('Could not clear the pending recalculation:', error);
  }
}

/** The recalculation the file says the cells owe the company the workbook belongs to; null when none. */
export function recalculationPendingInFile(settings: WorkbookSettings | null = workbookSettings()): PendingRecalculation | null {
  if (!settings) return null;
  let stored: Partial<PendingRecalculation> | null;
  try {
    stored = settings.get(RECALCULATION_PENDING_SETTING) as Partial<PendingRecalculation> | null;
  } catch {
    return null;
  }
  if (!stored || typeof stored.companyId !== 'string' || (stored.cause !== 'binding' && stored.cause !== 'move')) return null;
  return workbookCompany(settings)?.companyId === stored.companyId ? { companyId: stored.companyId, cause: stored.cause } : null;
}
